/**
 * Checking a ticket export against the taxonomy.
 *
 * TICKET CATEGORY IS THE ANCHOR. Four rules hang off it:
 *
 *   1. Category -> Status      the category permits a set of statuses
 *   2. Category -> Work Type   and a set of work types, which have nothing to
 *                              do with the statuses — the two lists are
 *                              independent and must not be cross-checked
 *   3. Category -> Issue Type  and a set of issue types
 *   4. Issue Type -> Sub-Issue the sub-issue belongs to the ISSUE TYPE, not to
 *                              the category. It tags along with the issue, so
 *                              it is checked against whatever issue type the
 *                              ticket claims — even when that issue type is
 *                              itself wrong for the category, because then the
 *                              two findings are different problems.
 *
 * "WRONG" AND "CANNOT BE CHECKED" ARE NOT THE SAME, and the difference is the
 * point of this module. The source spreadsheet is incomplete in places: three
 * categories list no work types at all, nine issue types list no sub-issues,
 * and a handful of rows belong to no category. Reporting those as violations
 * would flag hundreds of perfectly good tickets and train everybody to ignore
 * the report. They come back as `unknown` instead, counted separately, so a
 * gap in the rules reads as a gap in the rules.
 */

export interface Taxonomy {
  categories: string[];
  categoryAliases?: Record<string, string>;
  statuses: { name: string; categories: string[] }[];
  workTypes: { name: string; categories: string[] }[];
  issueTypes: { name: string; categories: string[]; subIssues: string[] }[];
}

/** Which column of the uploaded file holds each field. Indexes, because two
 *  columns can share a header and a name would then be ambiguous. */
export interface ColumnMap {
  category: number | null;
  status: number | null;
  workType: number | null;
  issueType: number | null;
  subIssue: number | null;
  reference: number | null;
}

export type Severity = 'violation' | 'unknown';

export interface Finding {
  field: 'category' | 'status' | 'workType' | 'issueType' | 'subIssue';
  severity: Severity;
  /** What the ticket said. */
  value: string;
  /** Why it was flagged, in the words the reader needs. */
  message: string;
}

export interface AuditRow {
  /** 1-based row number in the uploaded file, counting the header as row 1. */
  line: number;
  reference: string;
  category: string;
  status: string;
  workType: string;
  issueType: string;
  subIssue: string;
  findings: Finding[];
}

export interface AuditResult {
  rows: AuditRow[];
  checked: number;
  clean: number;
  withViolations: number;
  withUnknowns: number;
  /** Violations by field, for the summary strip. */
  byField: Record<Finding['field'], number>;
}

/* -------------------------------------------------------------------------
   Matching
   ------------------------------------------------------------------------- */

/**
 * Compare the way a human would: ignore case, collapse whitespace, drop
 * punctuation that the two spellings of a category disagree about.
 *
 * The source sheet names its categories twice and differs on spacing and
 * hyphens ("Datto  Alert" / "Datto Alert", "PreSales/Procurement" /
 * "Pre-Sales/Procurement"), and a PSA export will differ again. Matching on
 * the exact string would report every ticket in four categories as invalid.
 */
export const norm = (s: string): string =>
  s.toLowerCase().replace(/cleint/g, 'client').replace(/[^a-z0-9]/g, '');

const blank = (s: string): boolean => s.trim() === '';

/** An index of the taxonomy, built once per audit rather than per row. */
function index(tax: Taxonomy) {
  const aliasTo = new Map<string, string>();
  for (const [from, to] of Object.entries(tax.categoryAliases ?? {})) aliasTo.set(norm(from), to);

  const categoryByNorm = new Map<string, string>();
  for (const c of tax.categories) categoryByNorm.set(norm(c), c);
  for (const [k, v] of aliasTo) categoryByNorm.set(k, v);

  /** category -> the set of names allowed, for each of the three lists. */
  const allowed = (rows: { name: string; categories: string[] }[]) => {
    const m = new Map<string, Set<string>>();
    for (const c of tax.categories) m.set(c, new Set());
    for (const r of rows) {
      for (const raw of r.categories) {
        const cat = categoryByNorm.get(norm(raw));
        if (cat) m.get(cat)!.add(norm(r.name));
      }
    }
    return m;
  };

  const issueByNorm = new Map<string, { name: string; subIssues: string[] }>();
  for (const it of tax.issueTypes) issueByNorm.set(norm(it.name), it);

  return {
    categoryByNorm,
    statusFor: allowed(tax.statuses),
    workTypeFor: allowed(tax.workTypes),
    issueTypeFor: allowed(tax.issueTypes),
    issueByNorm,
    knownStatus: new Set(tax.statuses.map((s) => norm(s.name))),
    knownWorkType: new Set(tax.workTypes.map((w) => norm(w.name))),
  };
}

/* -------------------------------------------------------------------------
   The audit
   ------------------------------------------------------------------------- */

export function audit(headerRows: string[][], map: ColumnMap, tax: Taxonomy): AuditResult {
  const ix = index(tax);
  const at = (row: string[], col: number | null): string =>
    col === null ? '' : (row[col] ?? '').trim();

  const rows: AuditRow[] = headerRows.map((row, i) => {
    const category = at(row, map.category);
    const status = at(row, map.status);
    const workType = at(row, map.workType);
    const issueType = at(row, map.issueType);
    const subIssue = at(row, map.subIssue);
    const findings: Finding[] = [];

    const cat = ix.categoryByNorm.get(norm(category));

    /* The anchor first. Everything else is judged against the category, so an
       unrecognised one makes the rest unjudgeable rather than wrong — saying
       "status invalid" when we do not know the category would be a guess. */
    if (blank(category)) {
      findings.push({
        field: 'category',
        severity: 'violation',
        value: '',
        message: 'No ticket category. Every other rule hangs off it.',
      });
    } else if (!cat) {
      findings.push({
        field: 'category',
        severity: 'violation',
        value: category,
        message: `"${category}" is not one of the ${tax.categories.length} ticket categories.`,
      });
    }

    /* Rule 1 — status. */
    if (!blank(status)) {
      if (!ix.knownStatus.has(norm(status))) {
        findings.push({
          field: 'status',
          severity: 'violation',
          value: status,
          message: `"${status}" is not a known status.`,
        });
      } else if (cat) {
        const allow = ix.statusFor.get(cat)!;
        if (allow.size === 0) {
          findings.push({
            field: 'status',
            severity: 'unknown',
            value: status,
            message: `${cat} lists no permitted statuses, so this cannot be checked.`,
          });
        } else if (!allow.has(norm(status))) {
          findings.push({
            field: 'status',
            severity: 'violation',
            value: status,
            message: `"${status}" is not permitted for ${cat}.`,
          });
        }
      }
    }

    /* Rule 2 — work type. Independent of the status: a category's work types
       and its statuses are two separate lists and are never compared. */
    if (!blank(workType)) {
      if (!ix.knownWorkType.has(norm(workType))) {
        findings.push({
          field: 'workType',
          severity: 'violation',
          value: workType,
          message: `"${workType}" is not a known work type.`,
        });
      } else if (cat) {
        const allow = ix.workTypeFor.get(cat)!;
        if (allow.size === 0) {
          findings.push({
            field: 'workType',
            severity: 'unknown',
            value: workType,
            message: `${cat} lists no permitted work types, so this cannot be checked.`,
          });
        } else if (!allow.has(norm(workType))) {
          findings.push({
            field: 'workType',
            severity: 'violation',
            value: workType,
            message: `"${workType}" is not permitted for ${cat}.`,
          });
        }
      }
    }

    /* Rule 3 — issue type. */
    const issue = ix.issueByNorm.get(norm(issueType));
    if (!blank(issueType)) {
      if (!issue) {
        findings.push({
          field: 'issueType',
          severity: 'violation',
          value: issueType,
          message: `"${issueType}" is not a known issue type.`,
        });
      } else if (cat) {
        const allow = ix.issueTypeFor.get(cat)!;
        if (allow.size === 0) {
          findings.push({
            field: 'issueType',
            severity: 'unknown',
            value: issueType,
            message: `${cat} lists no permitted issue types, so this cannot be checked.`,
          });
        } else if (!allow.has(norm(issueType))) {
          findings.push({
            field: 'issueType',
            severity: 'violation',
            value: issueType,
            message: `"${issueType}" is not permitted for ${cat}.`,
          });
        }
      }
    }

    /* Rule 4 — sub-issue, against the ISSUE TYPE and never the category.
       Checked even when the issue type is wrong for the category: those are
       two different problems and collapsing them would hide one. */
    if (!blank(subIssue)) {
      if (!issue) {
        findings.push({
          field: 'subIssue',
          severity: 'unknown',
          value: subIssue,
          message: blank(issueType)
            ? 'No issue type on the ticket, so the sub-issue cannot be checked.'
            : `The issue type is not recognised, so "${subIssue}" cannot be checked.`,
        });
      } else if (issue.subIssues.length === 0) {
        findings.push({
          field: 'subIssue',
          severity: 'unknown',
          value: subIssue,
          message: `${issue.name} lists no sub-issues, so this cannot be checked.`,
        });
      } else if (!issue.subIssues.some((s) => norm(s) === norm(subIssue))) {
        findings.push({
          field: 'subIssue',
          severity: 'violation',
          value: subIssue,
          message: `"${subIssue}" does not belong to ${issue.name}.`,
        });
      }
    }

    return {
      /* +2: the header is line 1 and this array starts after it. */
      line: i + 2,
      reference: at(row, map.reference),
      category,
      status,
      workType,
      issueType,
      subIssue,
      findings,
    };
  });

  const byField: AuditResult['byField'] = {
    category: 0,
    status: 0,
    workType: 0,
    issueType: 0,
    subIssue: 0,
  };
  for (const r of rows) {
    for (const f of r.findings) if (f.severity === 'violation') byField[f.field] += 1;
  }

  const hasViolation = (r: AuditRow) => r.findings.some((f) => f.severity === 'violation');
  const hasUnknown = (r: AuditRow) => r.findings.some((f) => f.severity === 'unknown');

  return {
    rows,
    checked: rows.length,
    clean: rows.filter((r) => r.findings.length === 0).length,
    withViolations: rows.filter(hasViolation).length,
    withUnknowns: rows.filter((r) => !hasViolation(r) && hasUnknown(r)).length,
    byField,
  };
}

/* -------------------------------------------------------------------------
   Guessing the columns
   ------------------------------------------------------------------------- */

/** Header names a PSA is likely to use, most specific first — "sub issue"
 *  must beat "issue" or a Sub-Issue column would be claimed as the Issue. */
const HINTS: { field: keyof ColumnMap; patterns: RegExp[] }[] = [
  { field: 'subIssue', patterns: [/sub[\s_-]*issue/i, /sub[\s_-]*type/i] },
  { field: 'issueType', patterns: [/issue[\s_-]*type/i, /^issue$/i] },
  { field: 'workType', patterns: [/work[\s_-]*type/i, /^work$/i] },
  { field: 'category', patterns: [/ticket[\s_-]*categor/i, /^categor/i, /^type$/i] },
  { field: 'status', patterns: [/ticket[\s_-]*status/i, /^status/i] },
  { field: 'reference', patterns: [/ticket[\s_-]*(number|id|#)/i, /^(ticket|number|id|ref)$/i] },
];

/** Best guess at the mapping, for the user to correct. Ordered so the more
 *  specific fields claim their column before the looser patterns run. */
export function guessColumns(headers: string[]): ColumnMap {
  const map: ColumnMap = {
    category: null,
    status: null,
    workType: null,
    issueType: null,
    subIssue: null,
    reference: null,
  };
  const taken = new Set<number>();

  for (const { field, patterns } of HINTS) {
    for (const p of patterns) {
      const i = headers.findIndex((h, n) => !taken.has(n) && p.test(h.trim()));
      if (i >= 0) {
        map[field] = i;
        taken.add(i);
        break;
      }
    }
  }
  return map;
}
